<template>
  <ContextColorProvider context="wealth">
    <TwoColumnsContainerLoader v-if="isLoading" data-testid="wealth-loader" />
    <TwoColumnsContainer v-else>
      <template #main>
        <div>
          <WealthHeading
            head="Carteiras"
            :title="customerBalance.totalBalanceFormatted"
            :subtitle="customerBalance.totalBalanceUpdatedAtFormatted"
            :is-loading="balanceLoading"
            :has-error="hasBalanceError"
            @retry="setBalances"
          />
          <Performance>
            <template #actions>
              <WealthViewActions
                :is-customer-overdue="isOverdue"
                @create-portfolio="goToWalletCreate"
                @open-investment="goToInvestmentFlow"
              />
            </template>
          </Performance>
        </div>
      </template>

      <template #content>
        <WealthContent
          :show-create-wallet-button="!isSuitabilityPending"
          data-testid="wealth-content"
        />
      </template>
    </TwoColumnsContainer>
  </ContextColorProvider>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

import {
  TwoColumnsContainer,
  TwoColumnsContainerLoader
} from '@/modules/common/components/TwoColumnsContainer';
import { Feature } from '@/modules/common/components/Feature';
import { Performance } from './components/Performance';
import { PerformanceChart } from '@/modules/wealth/components/PerformanceChart';
import { WealthContent } from './components/WealthContent';
import { WealthChartAction } from '@/modules/wealth/components/WealthChartAction';
import { ContextColorProvider } from '@warrenbrasil/nebraska-web';
import { WealthViewActions } from './components/WealthViewActions';
import { WealthHeading } from '@/modules/wealth/components/WealthHeading';

import { IProfitValue } from '@/modules/wealth/services/investment/investment-types';

import { WarrenModules } from '@/types/navigation';
import { isSuitabilityPending } from '@/router/helpers/shouldRedirectToSuitability';
import { getCustomerBalance } from '../../services/investment';

import {
  trackCreateNewPortfolio,
  trackGoToInvestmentFlow,
  trackLoadPortfolios
} from './track-events';

const CustomerModule = namespace('CustomerModule');

@Component({
  components: {
    TwoColumnsContainer,
    TwoColumnsContainerLoader,
    ContextColorProvider,
    WealthContent,
    WealthChartAction,
    Feature,
    WealthViewActions,
    WealthHeading,
    Performance,
    PerformanceChart
  }
})
export default class WealthView extends Vue {
  private isLoading = true;
  private hasBalanceError = false;
  private balanceLoading = true;

  customerBalance = {
    totalBalanceFormatted: '',
    profitValues: [] as IProfitValue[],
    totalBalanceUpdatedAtFormatted: ''
  };

  @CustomerModule.Getter('isRegulatoryStatusBlocked')
  readonly isOverdue!: boolean;

  public readonly WarrenModules = WarrenModules;

  public readonly isSuitabilityPending: boolean = isSuitabilityPending();

  private async beforeMount() {
    await this.setBalances();

    this.isLoading = false;
    trackLoadPortfolios();
  }

  private async setBalances() {
    try {
      this.balanceLoading = true;
      this.hasBalanceError = false;

      const { customerBalance } = await this.getCustomerBalance();
      this.customerBalance = {
        totalBalanceFormatted: customerBalance.totalGrossWorth.formatted,
        profitValues: customerBalance.profitValues,
        totalBalanceUpdatedAtFormatted:
          customerBalance.totalBalanceUpdatedAtFormatted
      };
    } catch (error) {
      this.hasBalanceError = true;
    } finally {
      this.balanceLoading = false;
    }
  }

  private async getCustomerBalance(reset = true) {
    return await getCustomerBalance({
      isReset: reset
    });
  }

  private goToWalletCreate() {
    trackCreateNewPortfolio();

    this.$router.push({ name: 'catalog' });
  }

  private goToInvestmentFlow() {
    trackGoToInvestmentFlow();
  }
}
</script>

<style lang="less" scoped>
@navbar-height: 64px;

.wealth-view__warning {
  position: relative;
  margin-bottom: -@navbar-height;

  @media @tabletDown {
    @navbar-height: 56px;
    margin-bottom: -@navbar-height;
  }
}
</style>
